<script>
export default {
  props: {
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 10,
    },
    step: {
      type: Number,
      default: 1,
    },
    value: {
      type: [Number, String],
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      input: 0,
    }
  },
  computed: {
    valuePercentage() {
      return Number(((this.input - this.min) * 100) / (this.max - this.min))
    },
    position() {
      return 10 - this.valuePercentage * 0.2
    },
  },
  watch: {
    // initialization from the parent
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        this.input = newValue
      },
    },
    // two-way binding ( child to parent )
    input: {
      immediate: true,
      handler(newValue, oldValue) {
        this.$emit('input', this.input)
      },
    },
  },
}
</script>

<template>
  <div>
    <div :class="$style.tooltipContainer">
      <div :class="$style.rangeWrap">
        <div
          :style="{ left: `calc(${valuePercentage}% + (${position}px))` }"
          :class="$style.thumbInner"
          class="primary--text"
        >
        </div>
        <div
          :style="{ left: `calc(${valuePercentage}% + (${position}px))` }"
          :class="$style.rangeValue"
        >
          <span>{{ input }}</span>
        </div>
      </div>
    </div>

    <input
      v-model="input"
      v-bind="$attrs"
      :disabled="disabled"
      type="range"
      :min="min"
      :max="max"
      :step="step"
      :class="$style.slider"
    />
  </div>
</template>

<style lang="scss" module>
@import '@design';

.fill {
  position: relative;
  top: -12px;
  z-index: 0;
  width: 100%;
  height: 12px;
  border-radius: 40px;
}

.slider {
  display: block;
  width: 100%;
  margin: auto;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.slider::-webkit-slider-runnable-track {
  width: 100%;
  height: 12px;
  cursor: pointer;
  background: #4776e6; /* fallback for old browsers */
  background: linear-gradient(
    95.08deg,
    #00c7ff 2.49%,
    #ab2eff 46.38%,
    #ff3b25 97.19%
  );
  border-radius: 40px;
}

.slider:focus {
  outline: none;
}

.slider::-moz-range-track {
  height: 2px;
  background: rgba(59, 173, 227, 1);
  background: -moz-linear-gradient(
    45deg,
    rgba(59, 173, 227, 1) 0%,
    rgba(87, 111, 230, 1) 25%,
    rgba(152, 68, 183, 1) 51%,
    rgba(255, 53, 127, 1) 100%
  );
  background: -webkit-gradient(
    left bottom,
    right top,
    color-stop(0%, rgba(59, 173, 227, 1)),
    color-stop(25%, rgba(87, 111, 230, 1)),
    color-stop(51%, rgba(152, 68, 183, 1)),
    color-stop(100%, rgba(255, 53, 127, 1))
  );
  background: -webkit-linear-gradient(
    45deg,
    rgba(59, 173, 227, 1) 0%,
    rgba(87, 111, 230, 1) 25%,
    rgba(152, 68, 183, 1) 51%,
    rgba(255, 53, 127, 1) 100%
  );
  background: -o-linear-gradient(
    45deg,
    rgba(59, 173, 227, 1) 0%,
    rgba(87, 111, 230, 1) 25%,
    rgba(152, 68, 183, 1) 51%,
    rgba(255, 53, 127, 1) 100%
  );
  background: -ms-linear-gradient(
    45deg,
    rgba(59, 173, 227, 1) 0%,
    rgba(87, 111, 230, 1) 25%,
    rgba(152, 68, 183, 1) 51%,
    rgba(255, 53, 127, 1) 100%
  );
  background: linear-gradient(
    45deg,
    rgba(59, 173, 227, 1) 0%,
    rgba(87, 111, 230, 1) 25%,
    rgba(152, 68, 183, 1) 51%,
    rgba(255, 53, 127, 1) 100%
  );
  filter: progid:dximagetransform.microsoft.gradient( startColorstr='#3bade3 ', endColorstr='#ff357f ', GradientType=1 );
  -moz-appearance: none;
}

.slider::-webkit-slider-thumb {
  width: 32px;
  height: 32px;
  margin-top: -10px;
  cursor: -webkit-grab;
  background-color: $color-theme-light-offwhite;
  border: 2px solid $color-theme-light-primary-shadow;
  border-radius: 50%;
  -webkit-transition: border 1000ms ease;
  transition: border 1000ms ease;
  -webkit-appearance: none;
}

.slider::-webkit-slider-thumb:active {
  pointer-events: none;
  border: 2px solid $color-theme-light-secondary;
  -webkit-transform: scale(1.2);
  transform: scale(1.2);
}

.slider::-moz-range-thumb {
  position: relative;
  bottom: 11px;
  width: 25px;
  max-width: 80px;
  height: 25px;
  cursor: -moz-grab;
  background-color: #1d1c25;
  border: 2px solid;
  border-radius: 50%;
  -moz-transition: border 1000ms ease;
  transition: border 1000ms ease;
  -moz-appearance: none;
}

input[type='range']::-webkit-slider-thumb:active {
  cursor: -webkit-grabbing;
}

input[type='range']::-moz-range-thumb:active {
  cursor: -moz-grabbing;
}
.tooltipContainer {
  padding-right: 6px;
  padding-left: 6px;
}
.rangeWrap {
  position: relative;
  width: 100%;
}
.rangeValue {
  position: absolute;
  bottom: 70px;
}
.rangeValue span {
  position: absolute;
  left: 50%;
  display: block;
  width: 64px;
  height: 34px;
  padding-top: 5px;
  font-size: 12px;
  line-height: 24px;
  color: #fff;
  text-align: center;
  background: $color-theme-light-primary;
  border-radius: 16px;
  transform: translate(-50%, 0);
}
.rangeValue span::before {
  position: absolute;
  top: 100%;
  left: 50%;
  width: 0;
  height: 0;
  margin-top: -6px;
  margin-left: -7px;
  content: '';
  border-top: 10px solid $color-theme-light-primary;
  border-right: 7px solid transparent;
  border-left: 7px solid transparent;
}

.thumbInner {
  position: absolute;
  bottom: -13px;
  left: 50%;
  z-index: 2;
  height: 14px;
  pointer-events: none;
  border-left: 3px solid $color-theme-light-primary;
  border-radius: 9999px;
  transform: translate(-50%, 0);
}
</style>
